"use client";
/**
 * CartDrawerHost keeps shell-owned cart and appointment drawers mounted when
 * navigation controls are supplied by an external navigation fragment.
 * It consumes cart state from the shared providers and renders only the
 * merchant-specific drawer portals; the fragment remains responsible for the
 * visible cart button and delegates its click to `toggleCartDrawer`.
 */
import { ServiceSummary } from "@/app/(shop)/services/[slug]/components/serviceSummary";
import { useAuth } from "@/app/providers/authProvider";
import { useMerchant } from "@/app/providers/merchantProvider";
import { EditAddressesModal } from "@/components/editAddressesModal/editAddressesModal";
import { DeliveryModalError } from "@/components/deliveryModalError/deliveryModalError";
import { LoginInfoModal } from "@/components/loginInfoModal/loginInfoModal";
import { RestaurantCartDrawer } from "@/components/restaurantCartDrawer/restaurantCartDrawer";
import { ScanToOrderCart } from "@/components/scanToOrder/scanToOrderCart/scanToOrderCart";
import { ScanToOrderKitchenModal } from "@/components/scanToOrder/scanToOrderCart/scanToOrderKitchenModal";
import { RetailCartDrawer } from "@/components/cartDrawer/retailCartDrawer";
import { OrderType } from "@/constants/enum";
import { useCartDrawer } from "@/contexts/CartDrawerContext";
import { onDeliveryAddressUpdate } from "@/dataLayer/deliveryAddressUtils";
import type { GetMerchantPreferencesResponse } from "@/dataLayer/getMerchantPreferences";
import type { GetRetailPickupTimeResponse } from "@/dataLayer/getRetailPickupTime";
import type { Address } from "@/dto/address";
import type { DayPart } from "@/dto/dayPartMenu";
import type { TableData } from "@/dto/tableData";
import { RetailCart } from "@/models/cart/retailCart";
import { ServiceCart } from "@/models/cart/serviceCart";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { useCartDrawerRedirects } from "@/utils/hooks/useCartDrawerRedirects";
import { useNativeActions } from "@/utils/useNativeActions";
import { useMerchantColor } from "@/utils/hooks/useMerchantColor";
import useLocalStorage from "@/utils/useLocalStorage";
import { LocalStorageKey } from "@/utils/storageKeys";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import { use, useMemo, useState } from "react";

interface CartDrawerHostProps {
  merchantPreferences?: GetMerchantPreferencesResponse;
  pickupTimePromise?: Promise<GetRetailPickupTimeResponse | null>;
  selectedMenu?: DayPart | null;
}

export function CartDrawerHost({
  merchantPreferences,
  pickupTimePromise,
  selectedMenu = null,
}: Readonly<CartDrawerHostProps>) {
  const { merchant } = useMerchant();
  const { isLoggedIn } = useAuth();
  const {
    isRetailCartOpen,
    setIsRetailCartOpen,
    isRestaurantCartOpen,
    setIsRestaurantCartOpen,
    isServiceSummaryOpen,
    setIsServiceSummaryOpen,
    toggleCartDrawer,
    openCartDrawer,
    isHandlingCartDrawer,
    setIsHandlingCartDrawer,
    isProceedingToCheckout,
  } = useCartDrawer();
  const { isCmaApp, openLoginSignUp } = useNativeActions();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const { replace } = useRouter();
  const [storedGiftCard] = useLocalStorage(LocalStorageKey.GIFT_CARD, null);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
  const [toCheckoutAfterLogin, setToCheckoutAfterLogin] = useState(false);
  const [scanToOrderKitchenModalOpen, setScanToOrderKitchenModalOpen] = useState(false);
  const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] = useState(false);
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] = useState(false);
  const [tempAddress, setTempAddress] = useState<Address | string>(
    getFromLocalStorage(LocalStorageKey.DELIVERY_ADDRESS, "")
  );
  const emptyPickupTimePromise = useMemo(() => Promise.resolve(null), []);
  const retailPickupTime = use(pickupTimePromise ?? emptyPickupTimePromise);
  const tableData = getFromLocalStorage<TableData | undefined>(
    LocalStorageKey.TABLE_DATA,
    undefined
  );
  const scanToOrderInfo = getFromLocalStorage(LocalStorageKey.SCAN_TO_ORDER_INFO, {
    tableId: "",
    employeeId: "",
    orderType: OrderType.PICKUP,
  });
  const retailCart = RetailCart.getItems();
  const serviceCart = ServiceCart.getItems();
  const isScanToOrderFlow = Boolean(tableData && scanToOrderInfo);
  const queryParams = searchParams?.toString() ? `?${searchParams.toString()}` : "";
  const merchantColor = useMerchantColor();

  const handleLoginModal = (toCheckout: boolean) => {
    setIsRetailCartOpen(false);
    setToCheckoutAfterLogin(toCheckout);
    if (isCmaApp) {
      openLoginSignUp(toCheckout ? "checkoutButton" : "profileIcon");
    } else {
      setLoginModalIsOpen((isOpen) => !isOpen);
    }
  };

  useCartDrawerRedirects({
    merchant,
    pathName,
    queryParams,
    serviceCartLength: serviceCart.length,
    retailCartLength: retailCart.length,
    storedGiftCard,
    isRestaurantCartOpen: Boolean(isRestaurantCartOpen),
    isAppointmentSummaryOpen: isServiceSummaryOpen,
    isRetailCartOpen,
    isHandlingCartDrawer,
    setIsHandlingCartDrawer,
    isProceedingToCheckout,
    replace,
  });

  const shouldRenderRetailCart =
    merchant.isRetail ||
    merchant.isHybrid ||
    (merchant.isService && merchant.hasOnlyRetailItems) ||
    storedGiftCard;

  return (
    <>
      {shouldRenderRetailCart && (
        <RetailCartDrawer
          isOpen={isRetailCartOpen}
          onClose={() => setIsRetailCartOpen(false)}
          onRequestClose={toggleCartDrawer}
          merchant={merchant}
          onTriggerLoginModal={handleLoginModal}
          retailPickupTime={retailPickupTime}
          setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
          setIsRetailCartOpen={(value = false) => setIsRetailCartOpen(value)}
          setDeliveryAddressErrorModalOpen={setDeliveryAddressErrorModalOpen}
        />
      )}
      <ServiceSummary
        isOpen={isServiceSummaryOpen}
        onClose={() => setIsServiceSummaryOpen(false)}
        onRequestClose={toggleCartDrawer}
        merchant={merchant}
        onTriggerLoginModal={handleLoginModal}
        merchantPreferences={merchantPreferences}
        merchantColor={merchantColor}
        setIsRetailCartOpen={setIsServiceSummaryOpen}
      />
      {!tableData && merchant.isRestaurant && !isScanToOrderFlow && (
        <RestaurantCartDrawer
          isOpen={isRestaurantCartOpen}
          onClose={() => setIsRestaurantCartOpen(false)}
          onRequestClose={toggleCartDrawer}
          merchant={merchant}
          onTriggerLoginModal={handleLoginModal}
          setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
          setIsRestaurantCartOpen={setIsRestaurantCartOpen}
          setDeliveryAddressErrorModalOpen={setDeliveryAddressErrorModalOpen}
          selectedDayPart={selectedMenu}
        />
      )}
      {tableData && (
        <ScanToOrderCart
          isOpen={isRestaurantCartOpen}
          onRequestClose={toggleCartDrawer}
          merchant={merchant}
          tableData={tableData}
          setIsSentToKitchen={setScanToOrderKitchenModalOpen}
          scanToOrderInfo={scanToOrderInfo}
          isRunningTabsEnabled={merchant.isRunningTabsEnabled}
          selectedDayPart={selectedMenu}
        />
      )}
      <ScanToOrderKitchenModal
        isOpen={scanToOrderKitchenModalOpen}
        onRequestClose={() => setScanToOrderKitchenModalOpen(false)}
        merchant={merchant}
        scanToOrderInfo={scanToOrderInfo}
      />
      <EditAddressesModal
        setDefaultDeliveryAllowed={isLoggedIn}
        isOpen={deliveryAddressModalOpen}
        onRequestClose={() => setDeliveryAddressModalOpen(false)}
        onUpdated={async (address: Address, isDefaultDelivery: boolean) => {
          setTempAddress(address);
          await onDeliveryAddressUpdate(address, isDefaultDelivery, merchant.id, {
            openCartDrawer,
            setDeliveryAddressModalOpen,
            setDeliveryAddressErrorModalOpen,
          });
        }}
        checkoutCloseHandler={() => {}}
      />
      <DeliveryModalError
        onRequestClose={() => setDeliveryAddressErrorModalOpen(false)}
        isOpen={deliveryAddressErrorModalOpen}
        address={tempAddress}
        onEnterNewAddress={() => {
          setDeliveryAddressErrorModalOpen(false);
          setDeliveryAddressModalOpen(true);
        }}
        checkoutCloseHandler={() => {}}
      />
      <LoginInfoModal
        isOpen={loginModalIsOpen}
        onRequestClose={() => setLoginModalIsOpen(false)}
        merchantColor={merchantColor}
        toCheckoutAfterLogin={toCheckoutAfterLogin}
        merchant={merchant}
      />
    </>
  );
}
